import { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { PeopleOutlined } from "@mui/icons-material";
import { Employee } from "../../types/employee.types";
import { Shop } from "../../types/shop.types";
import { Schedule } from "../../types/schedule.types";
import { calculateTodayStaff } from "../../utils/dashboardCalculations";

type ViewMode = "daily" | "weekly";
type DailyGroupMode = "shop" | "employee";

interface TodayStaffCardProps {
  rawData: {
    employees: Employee[];
    shops: Shop[];
    schedules: Schedule[];
  };
  viewMode: ViewMode;
  dateRange: string[];
}

interface ShopGroup {
  shopId: string;
  shopName: string;
  shopColor: string;
  employees: { id: string; name: string; hours: number; times: string }[];
}

interface DayGroup {
  date: string;
  dayLabel: string;
  employees: { id: string; name: string; shopColor: string }[];
}

export default function TodayStaffCard({
  rawData,
  viewMode,
  dateRange,
}: TodayStaffCardProps) {
  const [dailyGroupMode, setDailyGroupMode] = useState<DailyGroupMode>("shop");

  // Calculate staff for the entire date range
  const staff = useMemo(
    () =>
      calculateTodayStaff(
        rawData.employees,
        rawData.shops,
        rawData.schedules,
        dateRange
      ),
    [rawData.employees, rawData.shops, rawData.schedules, dateRange]
  );

  // Group staff by shop for daily view
  const staffByShop = useMemo(() => {
    if (viewMode !== "daily") return [];

    const shopMap = new Map<string, ShopGroup>();

    staff.forEach((member) => {
      member.shifts.forEach((shift) => {
        let shopGroup = shopMap.get(shift.shopId);
        if (!shopGroup) {
          shopGroup = {
            shopId: shift.shopId,
            shopName: shift.shopName,
            shopColor: shift.shopColor,
            employees: [],
          };
          shopMap.set(shift.shopId, shopGroup);
        }

        // Check if employee already added to this shop
        if (!shopGroup.employees.find((e) => e.id === member.employeeId)) {
          const employeeShiftsInShop = member.shifts.filter(
            (s) => s.shopId === shift.shopId
          );
          const totalHours = employeeShiftsInShop.reduce(
            (sum, s) => sum + s.duration,
            0
          );
          const times = employeeShiftsInShop
            .map((s) => `${s.startTime}-${s.endTime}`)
            .join(", ");

          shopGroup.employees.push({
            id: member.employeeId,
            name: member.employeeName,
            hours: totalHours,
            times,
          });
        }
      });
    });

    return Array.from(shopMap.values()).sort((a, b) =>
      a.shopName.localeCompare(b.shopName)
    );
  }, [staff, viewMode]);

  // Group staff by day for weekly view
  const staffByDay = useMemo(() => {
    if (viewMode !== "weekly") return [];

    const dayGroups: DayGroup[] = dateRange.map((date) => {
      const dateObj = new Date(date + "T00:00:00");
      const dayLabel = dateObj.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });

      // Get staff working on this specific day
      const dayStaff = calculateTodayStaff(
        rawData.employees,
        rawData.shops,
        rawData.schedules,
        [date]
      );

      return {
        date,
        dayLabel,
        employees: dayStaff.map((member) => ({
          id: member.employeeId,
          name: member.employeeName,
          shopColor: member.shifts[0]?.shopColor || "#grey",
        })),
      };
    });

    return dayGroups;
  }, [rawData, dateRange, viewMode]);

  const title = viewMode === "daily" ? "Staff Scheduled" : "Staff Scheduled";
  const emptyMessage =
    viewMode === "daily"
      ? "No staff scheduled for this day"
      : "No staff scheduled for this week";

  const totalStaffCount =
    viewMode === "daily"
      ? staff.length
      : new Set(staffByDay.flatMap((d) => d.employees.map((e) => e.id))).size;

  return (
    <Paper
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 4,
          borderColor: "primary.light",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PeopleOutlined color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
          <Chip
            label={totalStaffCount}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        {viewMode === "daily" && staff.length > 0 && (
          <ToggleButtonGroup
            value={dailyGroupMode}
            exclusive
            onChange={(_, value) => value && setDailyGroupMode(value)}
            size="small"
          >
            <ToggleButton
              value="shop"
              sx={{
                px: 1.5,
                py: 0.25,
                fontSize: "0.75rem",
                textTransform: "none",
              }}
            >
              By Shop
            </ToggleButton>
            <ToggleButton
              value="employee"
              sx={{
                px: 1.5,
                py: 0.25,
                fontSize: "0.75rem",
                textTransform: "none",
              }}
            >
              By Employee
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {staff.length === 0 && viewMode === "daily" ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
          }}
        >
          <PeopleOutlined
            sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : viewMode === "daily" && dailyGroupMode === "shop" ? (
        // Daily view - Group by Shop
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {staffByShop.map((shop) => (
            <Box key={shop.shopId}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: shop.shopColor,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" fontWeight={600}>
                  {shop.shopName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({shop.employees.length})
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pl: 2.5 }}
              >
                {shop.employees.map((emp) => (
                  <Tooltip
                    key={emp.id}
                    title={`${emp.hours}h - ${emp.times}`}
                    placement="top"
                  >
                    <Chip
                      label={emp.name}
                      size="small"
                      sx={{
                        bgcolor: `${shop.shopColor}15`,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      ) : viewMode === "daily" && dailyGroupMode === "employee" ? (
        // Daily view - Group by Employee (show times)
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {staff.map((member) => (
            <Chip
              key={member.employeeId}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <span>{member.employeeName}</span>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                  >
                    {member.shifts
                      .map((s) => `${s.startTime}-${s.endTime}`)
                      .join(", ")}
                  </Typography>
                </Box>
              }
              size="small"
              sx={{
                bgcolor: member.shifts[0]?.shopColor
                  ? `${member.shifts[0].shopColor}20`
                  : "grey.100",
                borderLeft: member.shifts[0]?.shopColor
                  ? `3px solid ${member.shifts[0].shopColor}`
                  : "none",
                fontWeight: 500,
                height: "auto",
                "& .MuiChip-label": {
                  py: 0.5,
                },
              }}
            />
          ))}
        </Box>
      ) : (
        // Weekly view - Show by day
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {staffByDay.map((day) => (
            <Box key={day.date}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ minWidth: 100 }}
                >
                  {day.dayLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({day.employees.length})
                </Typography>
              </Box>
              {day.employees.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ pl: 1 }}
                >
                  No staff scheduled
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {day.employees.map((emp) => (
                    <Chip
                      key={emp.id}
                      label={emp.name}
                      size="small"
                      sx={{
                        bgcolor: emp.shopColor
                          ? `${emp.shopColor}15`
                          : "grey.100",
                        borderLeft: emp.shopColor
                          ? `3px solid ${emp.shopColor}`
                          : "none",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}
